import { motion } from "framer-motion";
import { useMemo } from "react";

/**
 * Slow-rising bubbles that drift up the hero. Communicates breath / calm /
 * mental motion without being distracting. Pure CSS + framer-motion, no canvas.
 */
const BUBBLE_COUNT = 18;

interface Bubble {
  id: number;
  left: number;       // 0-100 vw position
  size: number;       // px
  duration: number;   // s, full rise
  delay: number;      // s
  opacity: number;
  hue: "primary" | "accent";
  drift: number;      // px lateral sway
}

const buildBubbles = (): Bubble[] =>
  Array.from({ length: BUBBLE_COUNT }).map((_, i) => {
    // Deterministic pseudo-random so SSR/CSR match.
    const r = (n: number) => {
      const x = Math.sin(i * 9999 + n) * 10000;
      return x - Math.floor(x);
    };
    return {
      id: i,
      left: r(1) * 100,
      size: 14 + r(2) * 70,
      duration: 14 + r(3) * 14,
      delay: r(4) * -22,
      opacity: 0.15 + r(5) * 0.35,
      hue: r(6) > 0.55 ? "accent" : "primary",
      drift: (r(7) - 0.5) * 80,
    };
  });

const HeroBubbles = () => {
  const bubbles = useMemo(buildBubbles, []);

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
    >
      {bubbles.map((b) => (
        <motion.span
          key={b.id}
          className={`absolute rounded-full ${
            b.hue === "primary"
              ? "bg-primary/30 shadow-[0_0_24px_4px_hsl(var(--primary)/0.25)]"
              : "bg-accent/25 shadow-[0_0_24px_4px_hsl(var(--accent)/0.2)]"
          }`}
          style={{
            left: `${b.left}%`,
            bottom: -100,
            width: b.size,
            height: b.size,
            opacity: b.opacity,
          }}
          initial={{ y: 0, x: 0 }}
          animate={{
            y: -1100,
            x: [0, b.drift, -b.drift / 2, 0],
            opacity: [0, b.opacity, b.opacity, 0],
          }}
          transition={{
            duration: b.duration,
            delay: b.delay,
            repeat: Infinity,
            ease: "easeInOut",
            times: [0, 0.15, 0.85, 1],
          }}
        >
          {/* Inner highlight to give a glassy bubble feel */}
          <span
            className="absolute rounded-full bg-white/40 blur-[1px]"
            style={{
              top: "12%",
              left: "18%",
              width: "30%",
              height: "30%",
            }}
          />
        </motion.span>
      ))}
    </div>
  );
};

export default HeroBubbles;
